// ARIA — Clinical Voice Assistant helpers.
//
// TTS routing (2026-02 founder mandate): all spoken output goes through
// the backend-proxied ElevenLabs Rachel voice via /api/aria/tts. The
// browser SpeechSynthesis API is INTENTIONALLY not used — an iOS/desktop
// system voice must never be substituted for Rachel. If the backend
// reports TTS unavailable (missing config, upstream timeout, quota,
// etc.), speak() resolves silently and the caller is notified via the
// `onUnavailable` callback registered with `setTtsUnavailableHandler`;
// the UI then shows a "audio temporarily unavailable" banner while
// keeping the written response visible.
//
// listen()/recognition helpers still use the Web Speech Recognition API
// (mic capture) — that is unrelated to TTS and unchanged.

import { API } from "./api";

export const ARIA_SETTINGS_KEY = "aria_settings_v1";

export const DEFAULT_SETTINGS = {
  enabled: true,
  // Master voice-output toggle (Feb 2026 founder mandate). When false, ARIA
  // still LISTENS and renders text answers/greetings — only the Rachel MP3
  // playback is suppressed. Separate from `enabled` (which is the full
  // kill-switch for both mic and voice).
  spokenResponses: true,
  accent: "en-AU", // 'en-AU' | 'en-GB'
  rate: 0.92,
  pitch: 1.0,
  redFlagAlerts: true,
  topologyNarration: true,
  moduleGreetings: true,
  // Default OFF (P0 chairside-speed change, 2026-01):
  //   • New users: huddle does not auto-play at login — the mic tap remains
  //     the fastest possible interaction.
  //   • Existing users whose localStorage already has `aria_settings_v1` keep
  //     whatever value they previously saved (see loadSettings — persisted
  //     JSON is merged on top of DEFAULT_SETTINGS, so their choice wins).
  huddleAtLogin: false,
};

export function loadSettings() {
  try {
    const raw = localStorage.getItem(ARIA_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s) {
  try { localStorage.setItem(ARIA_SETTINGS_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

// ──────────────────────────────────────────────────────────────
// Voice selection — kept as a no-op stub so existing callers
// (AriaContext.selectVoice) keep working while the actual voice
// is centrally enforced by the backend proxy (Rachel only).
// ──────────────────────────────────────────────────────────────
export async function selectVoice(_preferredAccent = "en-AU") {
  return null;
}

// ──────────────────────────────────────────────────────────────
// Backend-proxied Rachel TTS  (chunked + replay + stop)
// ──────────────────────────────────────────────────────────────
let _audioEl = null;
let _unavailableHandler = null;
let _replayHandler = null;
let _playingHandler = null;
let _lastCancelToken = 0;
// Blob URLs for the LAST full response — used by replayLast(). Cleared and
// revoked at the start of every new speak() call and by clearReplay().
let _replayCache = [];

/**
 * Register a callback invoked whenever a speak() attempt fails because the
 * ElevenLabs proxy is unavailable (missing config, upstream error, quota).
 * The callback receives a `{ reason: string }` object matching the backend's
 * 503 body so the UI can render the correct copy.
 */
export function setTtsUnavailableHandler(cb) {
  _unavailableHandler = typeof cb === "function" ? cb : null;
}

/**
 * Register a callback invoked whenever the "replay last response"
 * availability changes. The callback receives a boolean — true when at
 * least one chunk of the last response is cached and can be replayed.
 */
export function setReplayAvailableHandler(cb) {
  _replayHandler = typeof cb === "function" ? cb : null;
}

/**
 * Register a callback invoked whenever playback starts/stops (used by the
 * UI to show the Stop Audio control).
 */
export function setPlayingHandler(cb) {
  _playingHandler = typeof cb === "function" ? cb : null;
}

function _notifyUnavailable(reason) {
  if (_unavailableHandler) {
    try { _unavailableHandler({ reason: reason || "upstream_error" }); } catch { /* ignore */ }
  }
}
function _notifyReplayAvailable(v) {
  if (_replayHandler) {
    try { _replayHandler(!!v); } catch { /* ignore */ }
  }
}
function _notifyPlaying(v) {
  if (_playingHandler) {
    try { _playingHandler(!!v); } catch { /* ignore */ }
  }
}

function _getAudioEl() {
  if (typeof window === "undefined") return null;
  if (_audioEl) return _audioEl;
  _audioEl = new Audio();
  _audioEl.preload = "auto";
  _audioEl.setAttribute("playsinline", "true");
  return _audioEl;
}

/**
/**
 * Prime the audio element for iOS Safari. Must be called from inside a
 * user-gesture handler (e.g. the FAB tap). A no-op elsewhere.
 * Silent MP3 is played+paused so subsequent programmatic `.play()` calls
 * during the same session are permitted by WebKit's autoplay policy.
 *
 * IDEMPOTENT (2026-02 founder mic-lag repair): once the audio element has
 * been unlocked in this tab, every subsequent call is a no-op. Re-priming
 * on every mic tap replays a silent MP3 that re-acquires the iOS audio
 * session and starves SR of the microphone for ~200-500ms — clipping the
 * clinician's first word on every follow-up.
 */
let _audioUnlocked = false;
export function primeAudio() {
  if (_audioUnlocked) return;
  const a = _getAudioEl();
  if (!a) return;
  try {
    // Minimal silent MP3 (~0.3s). Prevents cross-origin issues by using a
    // data URI, keeps the payload tiny, and never triggers a network call.
    a.src = "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf////////+/////9pQq5UKp1Kqf/////////+YSyq";
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        try { a.pause(); a.currentTime = 0; a.removeAttribute("src"); a.load(); } catch { /* ignore */ }
        _audioUnlocked = true;
      }).catch(() => { /* WebKit will still remember the gesture attempt */ });
    } else {
      _audioUnlocked = true;
    }
  } catch { /* best-effort */ }
}

export function isSpeechAvailable() {
  return typeof window !== "undefined" && typeof window.Audio !== "undefined";
}

/**
 * Hard-stop the current audio queue. Keeps the replay cache intact so the
 * user can still tap Replay after Stop. Called by the FAB's stop() flow
 * and by the new Stop Audio button in the answer card.
 *
 * Force-releases the iOS audio session by clearing `src` and calling
 * `.load()` — iOS otherwise holds the session for 100-300ms after pause(),
 * which starves the speech recogniser of the microphone and clips the
 * clinician's first word during a follow-up (2026-02 founder mic-lag repair).
 */
export function stopAudio() {
  _lastCancelToken += 1;
  const a = _audioEl;
  if (a) {
    try { a.pause(); } catch { /* ignore */ }
    try { a.currentTime = 0; } catch { /* ignore */ }
    try { a.onended = null; a.onerror = null; } catch { /* ignore */ }
    // Force iOS to release the audio session immediately. Without this
    // the mic tap after Rachel finishes routinely clips the first word.
    try { a.removeAttribute("src"); } catch { /* ignore */ }
    try { a.load(); } catch { /* ignore */ }
  }
  _notifyPlaying(false);
}

// Legacy alias — callers that used cancelSpeech() get the same behaviour
// (stop playback, but Replay remains available).
export const cancelSpeech = stopAudio;

/**
 * Discard the cached last-response chunks and revoke their blob URLs. Any
 * ongoing playback is stopped. UI observers are notified that Replay is
 * no longer available.
 */
export function clearReplay() {
  stopAudio();
  for (const url of _replayCache) {
    try { URL.revokeObjectURL(url); } catch { /* ignore */ }
  }
  _replayCache = [];
  _notifyReplayAvailable(false);
}

// ──────────────────────────────────────────────────────────────
// Sanitise clinical text before it reaches the TTS proxy so that
// URLs, citations, code blocks, and Markdown syntax are never spoken.
// The full WRITTEN answer stays intact — only the spoken stream is
// filtered.  Intentionally conservative: keeps clinical wording,
// numerals, and units untouched.
// ──────────────────────────────────────────────────────────────
export function sanitizeForSpeech(text) {
  if (!text || typeof text !== "string") return "";
  let s = text;
  // Fenced code blocks — drop entirely.
  s = s.replace(/```[\s\S]*?```/g, " ");
  // Inline code — keep the words, drop the backticks.
  s = s.replace(/`([^`]+)`/g, "$1");
  // Markdown image ![alt](url) → drop
  s = s.replace(/!\[[^\]]*\]\([^)]*\)/g, " ");
  // Markdown link [label](url) → keep label, drop url
  s = s.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  // Bare URLs
  s = s.replace(/https?:\/\/\S+/gi, " ");
  s = s.replace(/\bwww\.[^\s]+/gi, " ");
  // Numeric citations [1], [12], [3-5]
  s = s.replace(/\[\d+(?:[-,]\s*\d+)*\]/g, " ");
  // Markdown emphasis / headings / list markers / block quotes
  s = s.replace(/[*_~`>#]+/g, " ");
  s = s.replace(/^\s*[-•]\s+/gm, "");
  // Trailing "Sources:" / "References:" section — drop the label + list.
  s = s.replace(/\n\s*(sources?|references?|citations?)\s*:[\s\S]*$/i, "\n");
  // Collapse whitespace
  s = s.replace(/[ \t]+/g, " ").replace(/\s*\n\s*/g, "\n").trim();
  return s;
}

// ──────────────────────────────────────────────────────────────
// Clinical speech polish. Runs AFTER sanitizeForSpeech, BEFORE chunking.
// Only affects the SPOKEN stream — the written answer stays intact.
// Founder directive (2026-02 voice-first delivery):
//   • "35 Ncm" must be spoken "thirty-five Newton centimeters"
//   • "NP" / "RP" / "WP" must be spoken as separate platform letters
//   • Manufacturer / component names must remain readable to Rachel
//   • Never speak internal source_ids, code, or technical metadata
// ──────────────────────────────────────────────────────────────
export function polishForSpeech(text) {
  if (!text || typeof text !== "string") return "";
  let s = text;

  // Strip anything that looks like an internal registry / source id so it
  // never reaches TTS. Registry ids are lowercase snake_case with a
  // year-anchored tail ("nobel_biocare_ifu_rp_..._2024_p29"), and any
  // "source_id: xxx" style key/value.
  s = s.replace(/\bsource[_\s-]?id\s*[:=]\s*\S+/gi, " ");
  s = s.replace(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+){2,}\b/g, " ");

  // Torque unit — say the full phrase so Rachel pronounces
  // "35 Ncm" as "thirty-five Newton centimeters".
  s = s.replace(/(\d+(?:[.,]\d+)?)\s*Ncm\b/gi, "$1 Newton centimeters");
  s = s.replace(/\bNcm\b/g, "Newton centimeters");
  s = s.replace(/\bN[·\-\.]?cm\b/g, "Newton centimeters");

  // Implant platforms — spell out the letters so "RP" is heard as "R P",
  // never "arp". Only rewrite the bare token; anything already followed
  // by "platform" already reads correctly once split.
  s = s.replace(/\b([NRW])P(?=\s*(?:platform|abutment|screw|position|Nobel|conical|connection|implant)\b)/gi, "$1 P");
  s = s.replace(/\b([NRW])P\b(?=[\s.,;:!?)]|$)/g, "$1 P");

  // Well-known clinical abbreviations — spell each letter separately so
  // Rachel does not attempt to make them into words ("blx" → "blicks").
  const LETTER_ABBREV = [
    "IFU", "IFUs",
    "BLX", "BLT", "BL", "BLC",
    "CBCT", "STL", "PLY",
    "MDP", "TMD", "TMJ", "VDO", "OVD", "MIP",
    "FP1", "FP2", "FP3",
    "ISQ", "PMMA", "PEEK",
    "NC", "RC", "WB", "RB",
  ];
  for (const abbr of LETTER_ABBREV) {
    const spelled = abbr.split("").join(" ");
    s = s.replace(new RegExp(`\\b${abbr}\\b`, "g"), spelled);
  }

  // Common brand pronunciation nudges — light touch, only where Rachel
  // routinely mis-reads. Never rename the manufacturer.
  s = s.replace(/\bStraumann\b/g, "Straumann"); // no-op placeholder for future tuning
  s = s.replace(/\bNobelActive\b/g, "Nobel Active");
  s = s.replace(/\bNobelReplace\b/g, "Nobel Replace");
  s = s.replace(/\bNobelParallel\b/g, "Nobel Parallel");
  s = s.replace(/\bNobelProcera\b/g, "Nobel Procera");
  s = s.replace(/\bBioHorizons\b/g, "Bio Horizons");

  // Collapse whitespace we may have introduced.
  s = s.replace(/[ \t]+/g, " ").replace(/\s*\n\s*/g, "\n").trim();
  return s;
}

// ──────────────────────────────────────────────────────────────
// Chunk sanitised text into speakable segments.
//
// Voice-consistency directive (KISS revision, 2026-08-09): the vast
// majority of ARIA responses are shorter than ~1900 characters. Chunking
// a normal-length response into small segments causes ElevenLabs to
// re-seed the neural voice on every request, which produces the audible
// "Rachel drift" the founder is eliminating.  We therefore raise the
// ceiling to 1900 and take the whole-response early-return path first —
// only genuinely long clinical outputs will be split.
//
// When the polished text exceeds CHUNK_MAX we fall back to the previous
// paragraph → sentence → comma splitter so a single overlong response
// still speaks correctly.  Chunk floor is unchanged.
// ──────────────────────────────────────────────────────────────
const CHUNK_MAX = 1900;
const CHUNK_MIN = 40;

export function chunkForSpeech(text) {
  const sanitised = sanitizeForSpeech(text);
  const s = polishForSpeech(sanitised);
  if (!s) return [];
  // Whole-response early return — keep the entire polished stream in a
  // single ElevenLabs request whenever it fits under the ceiling. This
  // prevents mid-response voice drift on standard-length answers.
  if (s.length <= CHUNK_MAX) return [s];
  const paragraphs = s.split(/\n{1,}/).map((p) => p.trim()).filter(Boolean);
  const out = [];
  for (const para of paragraphs) {
    if (para.length <= CHUNK_MAX) { out.push(para); continue; }
    // Split into sentences.
    const sentences = para.match(/[^.!?]+(?:[.!?]+["')\]]*|$)/g) || [para];
    let buf = "";
    for (const rawSent of sentences) {
      const sent = rawSent.trim();
      if (!sent) continue;
      if (buf.length + sent.length + 1 <= CHUNK_MAX) {
        buf = buf ? `${buf} ${sent}` : sent;
      } else {
        if (buf) out.push(buf);
        // If a single sentence is longer than CHUNK_MAX, split on commas.
        if (sent.length > CHUNK_MAX) {
          const parts = sent.split(/,\s+/);
          let inner = "";
          for (const part of parts) {
            if (inner.length + part.length + 2 <= CHUNK_MAX) {
              inner = inner ? `${inner}, ${part}` : part;
            } else {
              if (inner) out.push(inner);
              inner = part;
            }
          }
          if (inner) out.push(inner);
          buf = "";
        } else {
          buf = sent;
        }
      }
    }
    if (buf) out.push(buf);
  }
  // Merge any trailing runt < CHUNK_MIN into the previous chunk.
  const merged = [];
  for (const c of out) {
    if (merged.length && c.length < CHUNK_MIN && (merged[merged.length - 1].length + c.length + 1) <= CHUNK_MAX) {
      merged[merged.length - 1] = `${merged[merged.length - 1]} ${c}`;
    } else {
      merged.push(c);
    }
  }
  return merged;
}

async function _fetchChunkBlob(chunk, token, cancelToken) {
  const res = await fetch(`${API}/aria/tts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({ text: chunk }),
  });
  if (cancelToken !== _lastCancelToken) throw { cancelled: true };
  if (res.status === 503) {
    let reason = "upstream_error";
    try {
      const j = await res.json();
      if (j && j.detail && j.detail.reason) reason = j.detail.reason;
    } catch { /* body may be empty */ }
    throw { reason };
  }
  if (!res.ok) throw { reason: "upstream_error" };
  return await res.blob();
}

function _playUrl(audio, url, cancelToken) {
  return new Promise((resolve) => {
    const cleanup = () => {
      audio.onended = null;
      audio.onerror = null;
    };
    audio.onended = () => {
      if (cancelToken !== _lastCancelToken) { cleanup(); resolve(false); return; }
      cleanup();
      resolve(true);
    };
    audio.onerror = () => {
      cleanup();
      resolve(false);
    };
    try {
      audio.src = url;
      const p = audio.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => { cleanup(); resolve(false); });
      }
    } catch { cleanup(); resolve(false); }
  });
}

/**
 * Speak `text` using the backend-proxied Rachel voice.
 *
 * The text is sanitised (URLs, citations, code blocks stripped), split
 * into short natural chunks, then streamed: chunk N+1 is fetched from
 * ElevenLabs while chunk N plays, so first-byte latency is low and the
 * queue never overlaps.
 *
 * Returns `true` on complete successful playback, `false` if TTS was
 * unavailable (in which case the `unavailableHandler` was invoked and
 * the caller must keep the "audio temporarily unavailable" banner
 * visible). Never falls back to SpeechSynthesis.
 */
export async function speak(text, _opts = {}) {
  if (!text || typeof text !== "string") return false;
  if (typeof window === "undefined") return false;

  const chunks = chunkForSpeech(text);
  if (!chunks.length) return true;

  // Cancel any in-flight playback and reset the replay cache.
  clearReplay();
  const myToken = ++_lastCancelToken;
  const audio = _getAudioEl();
  if (!audio) return false;

  const token = (() => {
    try { return localStorage.getItem("pa_token"); } catch { return null; }
  })();
  if (!token) return false;

  // Kick off fetch of the first chunk immediately.
  let pending = _fetchChunkBlob(chunks[0], token, myToken);
  let allOk = true;
  _notifyPlaying(true);
  try {
    for (let i = 0; i < chunks.length; i++) {
      if (myToken !== _lastCancelToken) { allOk = false; break; }
      let blob;
      try {
        blob = await pending;
      } catch (err) {
        if (err && err.cancelled) return false;
        _notifyUnavailable((err && err.reason) || "upstream_error");
        return false;
      }
      // Immediately start the next fetch so it overlaps playback of this chunk.
      if (i + 1 < chunks.length) {
        pending = _fetchChunkBlob(chunks[i + 1], token, myToken);
        // Suppress unhandled rejection if this chunk's playback fails / is
        // cancelled before we get to await it.
        pending.catch(() => { /* consumed below or discarded on cancel */ });
      }
      if (myToken !== _lastCancelToken) { allOk = false; break; }
      const url = URL.createObjectURL(blob);
      _replayCache.push(url);
      _notifyReplayAvailable(true);
      const ok = await _playUrl(audio, url, myToken);
      if (!ok) {
        if (myToken !== _lastCancelToken) { allOk = false; break; }
        _notifyUnavailable("playback_error");
        return false;
      }
    }
  } finally {
    _notifyPlaying(false);
  }
  return allOk;
}

/**
 * Replay the last full response using the cached MP3 chunks. Does NOT
 * re-hit the backend (and therefore never re-invokes the clinical model).
 * Returns false if nothing is cached or playback fails.
 */
export async function replayLast() {
  if (typeof window === "undefined") return false;
  if (!_replayCache.length) return false;
  // Cancel any current playback but keep the cache.
  stopAudio();
  const myToken = ++_lastCancelToken;
  const audio = _getAudioEl();
  if (!audio) return false;
  _notifyPlaying(true);
  try {
    for (const url of _replayCache) {
      if (myToken !== _lastCancelToken) return false;
      const ok = await _playUrl(audio, url, myToken);
      if (!ok) return false;
    }
  } finally {
    _notifyPlaying(false);
  }
  return true;
}

// ──────────────────────────────────────────────────────────────
// Listening (Web Speech Recognition — webkit only on most browsers)
// ──────────────────────────────────────────────────────────────
export function isRecognitionAvailable() {
  if (typeof window === "undefined") return false;
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

let _currentRecognition = null;

export function cancelRecognition() {
  try { _currentRecognition?.abort(); } catch { /* ignore */ }
  _currentRecognition = null;
}

export function listen({ onInterim, lang = "en-AU", timeoutMs = 10000 } = {}) {
  if (!isRecognitionAvailable()) {
    return Promise.reject(new Error("recognition_unavailable"));
  }
  return new Promise((resolve, reject) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = lang;
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.continuous = false;
    _currentRecognition = r;
    let final = "";
    const timer = setTimeout(() => {
      try { r.stop(); } catch { /* ignore */ }
    }, timeoutMs);
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else if (onInterim) onInterim(t);
      }
    };
    r.onerror = (e) => {
      clearTimeout(timer);
      _currentRecognition = null;
      reject(new Error(e.error || "recognition_error"));
    };
    r.onend = () => {
      clearTimeout(timer);
      _currentRecognition = null;
      resolve(final.trim());
    };
    try { r.start(); } catch (e) { reject(e); }
  });
}

// ──────────────────────────────────────────────────────────────
// Command vocabulary — order matters; first match wins.
// Each entry: { patterns: [string|regex], path, label, adminOnly? }
// ──────────────────────────────────────────────────────────────
export const ARIA_COMMANDS = [
  { patterns: ["new case", "quick assessment", "quick case"], path: "/quick-assessment/new", label: "Quick Assessment" },
  { patterns: ["full case analysis", "complete case", "case analysis"], path: "/case-analysis/new", label: "Complete Case Analysis" },
  { patterns: ["topology analysis", "open topology", "topology"], path: "/topology-analysis/new", label: "Topology Analysis" },
  { patterns: ["treatment plan", "open treatment"], path: "/treatment-plan/new", label: "Treatment Plan Engine" },
  { patterns: ["tmd", "tmd assessment", "joint assessment"], path: "/tmd-assessment/new", label: "TMD Assessment" },
  { patterns: ["perio prostho", "periodontal"], path: "/perio-prostho/new", label: "Perio-Prostho Planner" },
  { patterns: ["implant planner", "open implant", "implant case"], path: "/module/implant-case-planner", label: "Implant Case Planner" },
  { patterns: ["crown and bridge", "crown bridge", "fixed bridge"], path: "/module/crown-bridge-designer", label: "Crown & Bridge Designer" },
  { patterns: ["inlay", "onlay"], path: "/module/inlay-onlay-advisor", label: "Inlay/Onlay Advisor" },
  { patterns: ["smile design"], path: "/module/smile-design-planner", label: "Smile Design Planner" },
  { patterns: ["full arch", "all on x", "all-on-x", "all on four"], path: "/module/all-on-x-planner", label: "Full Arch All-on-X Planner" },
  { patterns: ["occlusion", "bite analysis"], path: "/module/occlusion-analyzer", label: "Occlusion Analyzer" },
  { patterns: ["denture", "rpd", "removable"], path: "/module/denture-rpd-planner", label: "Denture & RPD Planner" },
  { patterns: ["material", "material selection", "material science"], path: "/module/material-science-advisor", label: "Material Science Advisor" },
  { patterns: ["referral letter", "referral"], path: "/module/referral-letter-generator", label: "Referral Letter Generator" },
  { patterns: ["patient consent", "consent form", "consent"], path: "/module/patient-consent-generator", label: "Patient Consent Generator" },
  { patterns: ["fee estimate", "case fee", "estimate fee", "pricing"], path: "/module/case-fee-estimator", label: "Case Fee Estimator" },
  // Patient navigation — plural or singular "patient management" phrasing.
  { patterns: ["my patients", "patient list", "patients", "patient management", "manage patients", "open patient", "show patient"], path: "/patients", label: "Patients" },
  // Patient-entry phrases (create / add / enter). Route to the SAFE Patients
  // page (never directly to the new-patient form) and flag the intent so the
  // Aria layer can speak the no-PHI Preview explanation.
  { patterns: ["add a new patient", "add new patient", "new patient", "enter patient information", "enter patient info", "create a patient", "create patient"], path: "/patients", label: "Patients", isPatientEntry: true },
  { patterns: ["case history", "show my cases", "history"], path: "/history", label: "Case History" },
  { patterns: ["review queue", "my reviews"], path: "/admin/reviews", label: "Review Queue", adminOnly: true },
  { patterns: ["audit log", "audit logs"], path: "/admin/audit-logs", label: "Audit Logs", adminOnly: true },
  { patterns: ["security", "security ops", "security dashboard"], path: "/security-admin", label: "Security Ops", adminOnly: true },
  { patterns: ["dashboard", "home", "main page"], path: "/dashboard", label: "Dashboard" },
  { patterns: ["account", "settings", "profile"], path: "/account", label: "Account" },
];

export function matchCommand(transcript, { isAdmin = false } = {}) {
  if (!transcript) return null;
  const t = transcript.toLowerCase().trim();
  for (const cmd of ARIA_COMMANDS) {
    if (cmd.adminOnly && !isAdmin) continue;
    for (const p of cmd.patterns) {
      if (typeof p === "string") {
        if (t.includes(p)) return cmd;
      } else if (p instanceof RegExp) {
        if (p.test(t)) return cmd;
      }
    }
  }
  return null;
}

// ──────────────────────────────────────────────────────────────
// Per-module clinical orientation copy
// ──────────────────────────────────────────────────────────────
export const MODULE_GREETINGS = {
  "implant-case-planner":
    "For implant planning I will need the implant site, bone quality assessment, and restorative space. Shall I walk you through the workflow, or do you have a specific question?",
  "crown-bridge-designer":
    "Crown and bridge design — I will help you balance esthetics, occlusal forces, and material choice. Let us start with the prosthesis type and abutment situation.",
  "inlay-onlay-advisor":
    "Indirect restoration decision support. Tell me the cavity classification, remaining tooth structure, and occlusal load and I will guide the inlay, onlay, or full coverage decision.",
  "smile-design-planner":
    "Esthetic analysis time. I will need facial photographs, occlusal records, and the patient's esthetic concerns. Let us sequence the case together.",
  "all-on-x-planner":
    "Full arch rehabilitation requires careful consideration of vertical dimension, implant distribution, and prosthetic design. Let us begin with the patient presentation and bone availability.",
  "occlusion-analyzer":
    "This module analyzes occlusal scheme, TMD risk, and bite stability. Have the patient records ready and I will guide you through each assessment parameter.",
  "denture-rpd-planner":
    "Removable prosthodontics. I will help you with classification, retention design, and occlusal scheme. Start with the Kennedy class and remaining dentition.",
  "material-science-advisor":
    "Evidence-based material selection. Tell me the indication, esthetic demands, and occlusal load and I will recommend the optimal material.",
  "referral-letter-generator":
    "I will draft a clinically detailed referral letter. Give me the receiving specialist, reason for referral, and any pertinent findings.",
  "patient-consent-generator":
    "Informed consent drafting. I will produce a procedure-specific consent including risks, alternatives, and post-operative expectations.",
  "case-fee-estimator":
    "Treatment plan fee structuring. List the procedures and your practice context — I will provide a defensible fee range.",
};

export function greetingFor(slug) {
  return MODULE_GREETINGS[slug] || null;
}

// ──────────────────────────────────────────────────────────────
// Salutation
// ──────────────────────────────────────────────────────────────
export function timeGreeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

// Tokens that are NOT a surname — credentials, titles, common org suffixes.
const HONORIFIC_STOPWORDS = new Set([
  "dr", "doctor", "prof", "professor", "mr", "mrs", "ms", "miss",
  "dmd", "dds", "md", "mds", "phd", "msc", "ms", "ba", "bsc", "bds",
  "prosthodontist", "orthodontist", "periodontist", "endodontist",
  "dentist", "specialist", "consultant", "clinician",
  "inc", "llc", "ltd", "pllc", "pa",
  "camelionai", "prosthoai", "owner", "admin",
]);

const CRED_SUFFIX_RE = /,?\s*(dmd|dds|md|mds|phd|msc|bds|prosthodontist|orthodontist|periodontist|endodontist|owner|admin)\b\.?/gi;

export function honorific(fullName) {
  if (!fullName) return "Doctor";
  // Strip everything after a separator (· | — - / or 'at'/'@')
  let s = String(fullName).split(/[·|—•@/]| at /i)[0];
  // Strip credentials anywhere (DMD, DDS, MD, Prosthodontist…)
  s = s.replace(CRED_SUFFIX_RE, " ");
  // Strip parentheticals
  s = s.replace(/\([^)]*\)/g, " ");
  // Collapse whitespace
  s = s.replace(/[,.;]+/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return "Doctor";
  const tokens = s.split(" ").filter(Boolean);
  // Drop honorific prefixes from the front
  while (tokens.length && HONORIFIC_STOPWORDS.has(tokens[0].toLowerCase().replace(/\.$/, ""))) {
    tokens.shift();
  }
  // Drop trailing stopwords (credentials we missed, org names)
  while (tokens.length && HONORIFIC_STOPWORDS.has(tokens[tokens.length - 1].toLowerCase().replace(/\.$/, ""))) {
    tokens.pop();
  }
  if (!tokens.length) return "Doctor";
  // Surname = the last remaining alphabetic-only token (≥2 chars, ≥1 letter)
  for (let i = tokens.length - 1; i >= 0; i--) {
    const t = tokens[i].replace(/[^A-Za-z'-]/g, "");
    if (t.length >= 2 && /[A-Za-z]/.test(t)) {
      return `Dr. ${t}`;
    }
  }
  return "Doctor";
}

// ──────────────────────────────────────────────────────────────
// Login-greeting builders (KISS revision, 2026-08-09).
//
// One utterance → one canonical greeting. The visible toast and the
// spoken line share the SAME personalised honorific so a clinician
// hears exactly what they see.  Both helpers are pure so unit tests
// can exercise them without mounting React.
//
// buildVisibleGreeting(fullName)
//   → "Welcome back, {honorific}. ARIA is ready."
//
// buildSpokenGreeting(fullName, { body?, now? })
//   → "{time-greeting}, {honorific}. Welcome back. I am ARIA, your
//      ProsthoAI clinical assistant. {body || 'How may I assist you today?'}"
// ──────────────────────────────────────────────────────────────
export function buildVisibleGreeting(fullName) {
  return `Welcome back, ${honorific(fullName)}. ARIA is ready.`;
}

export function buildSpokenGreeting(fullName, { body, now } = {}) {
  const t = timeGreeting(now instanceof Date ? now : new Date());
  const h = honorific(fullName);
  const tail = (body && String(body).trim()) || "How may I assist you today?";
  return `${t}, ${h}. Welcome back. I'm ARIA, your ProsthoAI clinical assistant. ${tail}`;
}

// ──────────────────────────────────────────────────────────────
// Red flag scanner for topology / case reports
// ──────────────────────────────────────────────────────────────
const RED_FLAG_MARKERS = [
  /red[\s-]?flag/i,
  /⚠/,
  /\battention\b/i,
  /\bcontraindication\b/i,
  /\bsignificant risk\b/i,
  /\bcritical finding\b/i,
];

export function extractRedFlags(text, maxLen = 280) {
  if (!text) return [];
  const lines = String(text).split(/\n|\.\s+/);
  const hits = [];
  for (const line of lines) {
    if (RED_FLAG_MARKERS.some((rx) => rx.test(line))) {
      const trimmed = line.replace(/[*#>•⚠]/g, "").trim();
      if (trimmed.length > 6) hits.push(trimmed.slice(0, maxLen));
    }
    if (hits.length >= 3) break;
  }
  return hits;
}

// ──────────────────────────────────────────────────────────────
// Topology phase narration
// ──────────────────────────────────────────────────────────────
export const TOPOLOGY_PHASE_NARRATION = {
  queued: "Your case has been queued. Beginning topology analysis shortly.",
  classifying: "I am classifying case complexity and selecting the relevant clinical agents.",
  running_agents: "Running the specialist agents in parallel. Cross-referencing findings across periodontal, occlusal, and prosthetic domains.",
  synthesizing: "Synthesizing the final report from all agent outputs. This is the final step.",
};

// ──────────────────────────────────────────────────────────────
// Build a short spoken summary from a topology / case report
// ──────────────────────────────────────────────────────────────
export function spokenSummary(reportText, maxChars = 600) {
  if (!reportText) return "Analysis complete.";
  // Strip markdown syntax; collapse whitespace
  const clean = String(reportText)
    .replace(/```[\s\S]*?```/g, "")
    .replace(/[#*_>`~]/g, "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (clean.length <= maxChars) return clean;
  // First N chars, broken at the nearest period
  const slice = clean.slice(0, maxChars);
  const lastDot = slice.lastIndexOf(". ");
  return (lastDot > maxChars * 0.5 ? slice.slice(0, lastDot + 1) : slice) + " End of summary.";
}
